import AttachFileOutlined from '@mui/icons-material/AttachFileOutlined';
import { Box, Collapse, Tooltip } from '@mui/material';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import type { TLatestNewsItem } from './latestNewsStorage';
import { latestNewsAccordionCard as card } from './LatestNewsAccordionCard.style';
import { maybeFixUtf8Mojibake } from '~/utils/helper/encoding';
import { resolveOneBoxV3MediaUrl } from './latestNewsArticleMap';
import { blobDownload } from '~/utils/helper/downloadFile';

/** 左欄縮圖為 YouTube 靜態預覽圖時，疊灰色播放鈕提示可點開播放（與 `coverSrc` 的 img.youtube.com 一致） */
const isYoutubeThumbnailUrl = (src: string): boolean => {
  const s = src.trim();
  if (!s) return false;
  try {
    const host = new URL(s).hostname.toLowerCase();
    return host === 'img.youtube.com' || host === 'i.ytimg.com';
  } catch {
    return /(?:img\.youtube\.com|i\.ytimg\.com)\//i.test(s);
  }
};

/** 主檔名超過此字數（不含副檔名）時，顯示為「前五字…副檔名」 */
const ATTACHMENT_DISPLAY_NAME_MAX = 5;

/**
 * 附件連結上顯示的檔名：主檔名 &gt; 5 字 → 前 5 字 + … + 副檔名；完整檔名請用 Tooltip。
 */
const truncateAttachmentDisplayName = (fileName: string): string => {
  const fixed = maybeFixUtf8Mojibake(fileName).trim();
  if (!fixed) return fixed;
  const lastDot = fixed.lastIndexOf('.');
  const hasExt =
    lastDot > 0 &&
    lastDot < fixed.length - 1 &&
    !fixed.slice(0, lastDot).includes('/');
  if (!hasExt) {
    return fixed.length <= ATTACHMENT_DISPLAY_NAME_MAX
      ? fixed
      : `${fixed.slice(0, ATTACHMENT_DISPLAY_NAME_MAX)}...`;
  }
  const base = fixed.slice(0, lastDot);
  const extWithDot = fixed.slice(lastDot);
  if (base.length <= ATTACHMENT_DISPLAY_NAME_MAX) return fixed;
  return `${base.slice(0, ATTACHMENT_DISPLAY_NAME_MAX)}...${extWithDot}`;
};

type TProps = {
  row: TLatestNewsItem;
  expanded: boolean;
  onToggleExpand: () => void;
  /** 封面縮圖 URL；無後台圖時由外層帶入 public 佔位圖 */
  thumb: string;
  /** 依排序顯示之多檔附件 */
  attachments: { url: string; fileName: string; sizeBytes: number }[];
  /** 點封面：可能是圖片放大或影片播放（由父層決定） */
  onCoverClick?: () => void;
};

export const LatestNewsAccordionCard = ({
  row,
  expanded,
  onToggleExpand,
  thumb,
  attachments,
  onCoverClick,
}: TProps) => {
  const collapsedBodyRef = useRef<HTMLDivElement | null>(null);
  const [isCollapsible, setIsCollapsible] = useState(false);

  useEffect(() => {
    if (expanded) return;
    const el = collapsedBodyRef.current;
    if (!el) return;

    const compute = () => {
      setIsCollapsible(el.scrollHeight > el.clientHeight + 1);
    };

    compute();

    const ro = new ResizeObserver(() => compute());
    ro.observe(el);
    return () => ro.disconnect();
  }, [row.id, row.contentHtml, expanded]);

  const showToggleButton = isCollapsible || expanded;
  const showYoutubePlayOverlay = isYoutubeThumbnailUrl(thumb);

  const toExternalAttachmentUrl = (rawUrl: string): string => {
    const normalized = resolveOneBoxV3MediaUrl(rawUrl).trim();
    if (/^https?:\/\//i.test(normalized)) return normalized;

    const apiBase = String(
      import.meta.env.VITE_ONEBOX3_API_DOMAIN || '',
    ).trim();
    const webBase = String(import.meta.env.VITE_ONEBOX_DOMAIN || '').trim();
    const fallbackBase = apiBase || webBase;
    if (!fallbackBase) return normalized;
    return `${fallbackBase.replace(/\/$/, '')}/${normalized.replace(/^\//, '')}`;
  };

  const handleAttachmentDownload = (
    e: MouseEvent<HTMLAnchorElement>,
    attachment: { url: string; fileName: string; sizeBytes: number },
  ) => {
    e.preventDefault();
    const sourceUrl = toExternalAttachmentUrl(attachment.url);
    const filename =
      maybeFixUtf8Mojibake(attachment.fileName).trim() || '附件下載';
    if (!sourceUrl) return;
    blobDownload(sourceUrl, filename);
  };

  return (
    <card.Root elevation={0}>
      <card.Inner>
        <card.Row direction="row">
          {/* Left media */}
          <card.LeftColumn>
            <card.Cover
              onClick={onCoverClick}
              onKeyDown={e => {
                if (!onCoverClick) return;
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                onCoverClick();
              }}
              role={onCoverClick ? 'button' : undefined}
              tabIndex={onCoverClick ? 0 : undefined}
            >
              <card.CoverMedia>
                <card.CoverImage src={thumb} alt="" />
                {showYoutubePlayOverlay ? (
                  <card.YoutubePlayOverlay aria-hidden>
                    <card.YoutubePlayCircle>
                      <card.YoutubePlayTriangle />
                    </card.YoutubePlayCircle>
                  </card.YoutubePlayOverlay>
                ) : null}
              </card.CoverMedia>
            </card.Cover>

            {row.tags?.length ? (
              <card.TagsWrap>
                {row.tags.map(t => (
                  <card.Tags key={t}>#{t}</card.Tags>
                ))}
              </card.TagsWrap>
            ) : null}

            <card.Date>
              上架日期：{row.publishedAt.replaceAll('-', '/')}
            </card.Date>
          </card.LeftColumn>

          {/* Right content */}
          <card.RightColumn>
            <card.TitleBar>
              <card.Title component="h2">{row.title}</card.Title>
            </card.TitleBar>

            <card.BodyShell>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: expanded ? '1 1 auto' : '0 1 auto',
                  minHeight: 0,
                  ...(expanded
                    ? {
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        pr: 0.5,
                      }
                    : {}),
                }}
              >
                <card.BodyUpper>
                  <card.BodyGrow>
                    {!expanded ? (
                      <card.CollapsedBody
                        ref={collapsedBodyRef}
                        className="latest-news-body"
                        dangerouslySetInnerHTML={{
                          __html: row.contentHtml ?? '',
                        }}
                      />
                    ) : (
                      <Collapse
                        in={expanded}
                        timeout="auto"
                        unmountOnExit={false}
                      >
                        <card.ExpandedBody
                          className="latest-news-body"
                          dangerouslySetInnerHTML={{
                            __html: row.contentHtml ?? '',
                          }}
                        />
                      </Collapse>
                    )}
                  </card.BodyGrow>

                  {attachments.length > 0 ? (
                    <card.AttachmentList>
                      {attachments.map((attachment, idx) =>
                        attachment.url ? (
                          <card.AttachmentRow key={`${attachment.url}-${idx}`}>
                            <card.AttachmentIcon className="latest-news-attachmentIcon">
                              <AttachFileOutlined sx={{ fontSize: 18 }} />
                            </card.AttachmentIcon>
                            <Tooltip
                              title={maybeFixUtf8Mojibake(attachment.fileName)}
                              placement="top"
                              enterDelay={400}
                            >
                              <card.AttachmentLink
                                className="latest-news-attachmentLink"
                                href={attachment.url}
                                onClick={e =>
                                  handleAttachmentDownload(e, attachment)
                                }
                                underline="none"
                              >
                                {truncateAttachmentDisplayName(
                                  attachment.fileName,
                                )}
                              </card.AttachmentLink>
                            </Tooltip>
                          </card.AttachmentRow>
                        ) : null,
                      )}
                    </card.AttachmentList>
                  ) : null}
                </card.BodyUpper>
              </Box>

              {!expanded ? <card.BodySpacer /> : null}

              {showToggleButton ? (
                <card.ButtonWrap>
                  <card.ToggleButton
                    fullWidth
                    disableElevation
                    variant="contained"
                    onClick={onToggleExpand}
                  >
                    {expanded ? '收合文章' : '繼續閱讀'}
                  </card.ToggleButton>
                </card.ButtonWrap>
              ) : null}
            </card.BodyShell>
          </card.RightColumn>
        </card.Row>
      </card.Inner>
    </card.Root>
  );
};
